import { orchestrator } from 'satcheljs';
import { calendarCacheInitializedForAccount } from 'owa-calendar-cache-loader';
import {
    selectedCalendarsUpdated,
    getSelectedCalendarsCount,
    getSelectedCalendarsForUser,
} from 'owa-calendar-module-selected-calendars-user-config';
import { initialize } from '../actions/internalActions';
import { autoSelectDefaultCalendar } from './autoSelectDefaultCalendar';
import { getPrimaryAndConnectedAccountsEmailAddresses } from 'owa-session-store';
import { isLoaded, CalendarEndpointType } from 'owa-calendar-cache-loader';
import { getUserMailboxInfo } from 'owa-client-ids';
import { getCalendarEntryByCalendarId } from 'owa-calendar-cache';

/**
 * We want to auto-select calendars in two cases:
 * 1. We do not have any selected calendars for an account on load. In this case, we select the
 * default calendar for the account.
 * 2. The user unselects their last selected calendar for any account (i.e. they have 0 selected calendars across all their accounts).
 * In this case we want to auto-select the primary account default calendar.
 */

/**
 * We are able to auto select a calendar for an account when the calendar cache is loaded for this account,
 * so when the calendar cache is initialized for an account, we check if we want to auto select
 */
export const calendarCacheInitializedForAccountOrchestrator = orchestrator(
    calendarCacheInitializedForAccount,
    ({ userId, calendarEndpointType }) => {
        if (
            !hasValidSelectedCalendars(userId) &&
            calendarEndpointType ===
                CalendarEndpointType.Outlook /* The selected calendars property stores selected group and calendar mailbox
        calendar ids together. However, the default calendar is associated with/ loaded with the outlook mailbox calendars.
        So, we only autoselect a default calendar when data is loaded from the outlook endpoint */
        ) {
            autoSelectDefaultCalendar(userId);
        }
    }
);

/**
 * When this module loads, we auto select as needed for the accounts that are loaded
 */
export const initializeOrchestrator = orchestrator(initialize, () => {
    getPrimaryAndConnectedAccountsEmailAddresses().forEach(smtpAddress => {
        if (
            isLoaded(CalendarEndpointType.Outlook, smtpAddress) &&
            !hasValidSelectedCalendars(smtpAddress)
        ) {
            autoSelectDefaultCalendar(smtpAddress);
        }
    });
});

/**
 * Checks if an account has any valid selected calendars. In other words, it checks the user has selected calendars that are in the
 * cache. This should only be used after the calendar cache is loaded for smtpAddress.
 * @param smtpAddress
 */
function hasValidSelectedCalendars(smtpAddress: string) {
    return getSelectedCalendarsForUser(smtpAddress)
        .map(getCalendarEntryByCalendarId)
        .some(calendar => !!calendar);
}

/**
 * When a user updates the selected calendars, if there are no selected calendars
 * select the default calendar of the primary account.
 */
export const onSelectedCalendarsUpdatedOrchestrator = orchestrator(selectedCalendarsUpdated, () => {
    const smtpAddress = getUserMailboxInfo().userIdentity;
    if (getSelectedCalendarsCount() === 0 && isLoaded(CalendarEndpointType.Outlook, smtpAddress)) {
        autoSelectDefaultCalendar(smtpAddress);
    }
});
